import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const HardnessReportDetail: React.FC<{
  heatNumber: string;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeLocation, setActiveLocation] = useState('HB-WELD');
  type HardnessReadingRow = {
    id: string;
    readingId: string;
    location: string;
    groupId: string;
    position: string;
    valueHv10: number;
    projectLimit: number;
    fallbackLimit: number;
    status: string;
    measurement: ReportMeasurementItem;
  };
  const getHardnessLimit = (fieldPath: string, fallback: number) => {
    const limit = projectLimitFields.find((field) => field.fieldPath === fieldPath);
    return {
      value: limit?.max ?? fallback,
      text: limit?.limit || `<= ${fallback} HV10`,
    };
  };
  const overallLimit = getHardnessLimit('overall_max', HARDNESS_DETAIL.requirements.overallMaxHv10);
  const weldLimit = getHardnessLimit('weld_hv10', overallLimit.value);
  const hazLimit = getHardnessLimit('haz_hv10', overallLimit.value);
  const bodyLimit = getHardnessLimit('body_hv10', overallLimit.value);
  const getLimitForZone = (zone: string) => {
    const normalized = zone.toLowerCase();
    if (normalized.includes('haz')) return hazLimit;
    if (normalized.includes('base') || normalized.includes('body')) return bodyLimit;
    if (normalized.includes('weld')) return weldLimit;
    return overallLimit;
  };
  const getHardnessStatus = (value: number, zone: string) => value <= getLimitForZone(zone).value ? 'PASS' : 'FAIL';
  const isNumericHardnessId = (value?: string) => Boolean(value && (/^\d{5,}$/.test(value) || /^measure[-\s]?\d+$/i.test(value)));
  const getHardnessReadingLabel = (reading: HardnessReadingRow) => {
    if (reading.position) return formatLabel(reading.position);
    if (reading.readingId && !isNumericHardnessId(reading.readingId)) return reading.readingId;
    return 'Indent reading';
  };
  const getHardnessScaleBounds = (value: number, zoneLimit: number) => {
    const ceiling = Math.max(zoneLimit, fallbackLimit, value);
    const max = Math.ceil(ceiling + Math.max(10, ceiling * 0.05));
    const min = Math.max(0, Math.floor(Math.min(zoneLimit * 0.6, value > 0 ? value * 0.85 : zoneLimit * 0.6, zoneLimit - 50)));
    return { min, max };
  };
  const projectLimit = overallLimit.value;
  const fallbackLimit = HARDNESS_DETAIL.requirements.standardMaxHv10;
  const projectLimitRows: Array<[string, React.ReactNode, boolean?]> = projectLimitFields.length
    ? projectLimitFields.map((field) => [field.label, field.limit, true])
    : [
      ['Method', HARDNESS_DETAIL.method, true],
      ['Overall max', overallLimit.text, true],
      ['Weld max', weldLimit.text, true],
      ['HAZ max', hazLimit.text, true],
      ['Body max', bodyLimit.text, true],
    ];
  const baseReadings: HardnessReadingRow[] = HARDNESS_DETAIL.locations.flatMap((group) =>
    group.readings.filter((reading) => !isSampleDataDeleted(deletedMeasurementIds, `hardness-${group.id}-${reading.id}`)).map((reading) => ({
      ...reading,
      id: `hardness-${group.id}-${reading.id}`,
      readingId: reading.id,
      location: group.location,
      groupId: group.id,
      valueHv10: reading.valueHv10,
      projectLimit: getLimitForZone(group.location).value,
      fallbackLimit,
      status: getHardnessStatus(reading.valueHv10, group.location),
      measurement: createSampleMeasurement('HARDNESS', `hardness-${group.id}-${reading.id}`, 'Hardness reading', reading.valueHv10, 'HV10', `Project ${getLimitForZone(group.location).text}; API fallback <= ${fallbackLimit} HV10`, getHardnessStatus(reading.valueHv10, group.location), {
        fieldPath: 'hardness_reading',
        frequency: frequency || 'per_heat',
        reference: HARDNESS_DETAIL.sourceFile,
        hardnessZone: group.location,
        hardnessReadingId: reading.id,
        hardnessPosition: reading.position,
        hardnessValueHv10: String(reading.valueHv10),
        hardnessProjectLimitHv10: String(getLimitForZone(group.location).value),
        hardnessFallbackLimitHv10: String(fallbackLimit),
      }),
    }))
  );
  const userReadings: HardnessReadingRow[] = measurements.filter((measurement) => measurement.reportType === 'HARDNESS').map((measurement) => {
    const zone = measurement.hardnessZone || 'weld cap';
    const groupId = `USER-${zone.toUpperCase().replace(/\s+/g, '-')}`;
    return {
      id: measurement.id,
      readingId: measurement.hardnessReadingId || measurement.fieldSymbol || 'USER',
      location: zone,
      groupId,
      position: measurement.hardnessPosition || 'user indent',
      valueHv10: Number(measurement.hardnessValueHv10 || measurement.measuredValue || 0),
      projectLimit: Number(measurement.hardnessProjectLimitHv10 || getLimitForZone(zone).value),
      fallbackLimit: Number(measurement.hardnessFallbackLimitHv10 || fallbackLimit),
      status: measurement.status === 'PENDING_REVIEW' ? getHardnessStatus(Number(measurement.hardnessValueHv10 || measurement.measuredValue || 0), zone) : measurement.status,
      measurement,
    };
  });
  const allReadings = [...baseReadings, ...userReadings];
  const locationReadings = allReadings.filter((reading) => reading.groupId === activeLocation || reading.location === activeLocation);
  const activeReadings = locationReadings.length > 0 ? locationReadings : allReadings.filter((reading) => reading.location === 'weld cap');
  const zoneRows = ['weld cap', 'weld root', 'haz outer', 'haz inner', 'base metal'].map((zone) => {
    const readings = allReadings.filter((reading) => reading.location === zone);
    const max = readings.length ? Math.max(...readings.map((reading) => reading.valueHv10)) : 0;
    const mean = readings.length ? readings.reduce((sum, reading) => sum + reading.valueHv10, 0) / readings.length : 0;
    const status = readings.some((reading) => reading.status === 'FAIL')
      ? 'FAIL'
      : readings.some((reading) => reading.status === 'PENDING_REVIEW')
        ? 'PENDING_REVIEW'
        : 'PASS';
    return { zone, readings, max, mean, status };
  });
  const readingValues = allReadings.map((reading) => reading.valueHv10);
  const maxReading = readingValues.length ? Math.max(...readingValues) : 0;
  const failedCount = allReadings.filter((reading) => reading.status === 'FAIL').length;
  const averageReading = readingValues.length ? allReadings.reduce((sum, reading) => sum + reading.valueHv10, 0) / allReadings.length : 0;
  const minReading = readingValues.length ? Math.min(...readingValues) : 0;
  const overallStatus = failedCount > 0 ? 'FAIL' : 'PASS';
  const getZoneReading = (zone: string, index: number) => allReadings.filter((reading) => reading.location === zone)[index];
  const crossSectionXs = [80, 130, 188, 220, 240, 260, 290, 348, 400, 440];
  const crossSectionRows = [
    {
      key: 'cap',
      label: 'cap',
      y: 52,
      slots: [
        getZoneReading('base metal', 0),
        getZoneReading('base metal', 1),
        getZoneReading('haz outer', 0),
        getZoneReading('weld cap', 0),
        getZoneReading('weld cap', 1),
        getZoneReading('weld cap', 2),
        getZoneReading('haz inner', 0),
        getZoneReading('base metal', 2),
        getZoneReading('base metal', 3),
        getZoneReading('base metal', 4),
      ],
    },
    {
      key: 'mid',
      label: 'mid',
      y: 68,
      slots: [
        getZoneReading('base metal', 0),
        getZoneReading('base metal', 1),
        getZoneReading('haz outer', 1),
        getZoneReading('weld cap', 3),
        getZoneReading('weld cap', 4),
        getZoneReading('weld root', 0),
        getZoneReading('haz inner', 1),
        getZoneReading('base metal', 2),
        getZoneReading('base metal', 3),
        getZoneReading('base metal', 4),
      ],
    },
    {
      key: 'root',
      label: 'root',
      y: 84,
      slots: [
        getZoneReading('base metal', 0),
        getZoneReading('base metal', 1),
        getZoneReading('haz outer', 2),
        getZoneReading('weld root', 1),
        getZoneReading('weld root', 2),
        getZoneReading('weld root', 3),
        getZoneReading('haz inner', 2),
        getZoneReading('base metal', 2),
        getZoneReading('base metal', 3),
        getZoneReading('base metal', 4),
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <StatCard label="Max HV" value={maxReading} sub={`limit ${overallLimit.text}`} status={overallStatus} />
          <StatCard label="Avg HV" value={Math.round(averageReading)} sub={`${allReadings.length} readings`} status="PASS" />
          <StatCard label="Min HV" value={minReading} sub="pipe body" status="PASS" />
          <StatCard label="Method" value={HARDNESS_DETAIL.method} sub={frequency || 'per_heat'} status={overallStatus} />
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="HV10 weld cross-section" subtitle={`${zoneRows.length} zones - sour-service max is the controlling criteria`} />
            <div className="p-5">
              <div className="mx-auto max-w-[760px] rounded border border-[#C9D1DA] bg-white p-3">
                <svg viewBox="0 0 480 130" className="h-[160px] w-full">
                  <rect x="0" y="40" width="480" height="50" fill="#EEF0F3" stroke="#D8DEE6" />
                  <path d="M 200 40 L 280 40 L 270 90 L 210 90 Z" fill="#DCEEF8" stroke="#5A86A7" />
                  <rect x="180" y="40" width="20" height="50" fill="#F7E8B6" opacity="0.65" />
                  <rect x="280" y="40" width="20" height="50" fill="#F7E8B6" opacity="0.65" />

                  <text x="100" y="35" fontFamily="monospace" fontSize="9" fill="#7A8490" textAnchor="middle">base metal</text>
                  <text x="190" y="35" fontFamily="monospace" fontSize="9" fill="#7A8490" textAnchor="middle">haz</text>
                  <text x="240" y="35" fontFamily="monospace" fontSize="9" fill="#7A8490" textAnchor="middle">weld</text>
                  <text x="290" y="35" fontFamily="monospace" fontSize="9" fill="#7A8490" textAnchor="middle">haz</text>

                  {crossSectionRows.map((row) => (
                    <React.Fragment key={row.key}>
                      <text x="14" y={row.y + 4} fontFamily="monospace" fontSize="8" fill="#4B5563">{row.label}</text>
                      {row.slots.map((reading, index) => {
                        const isFail = reading?.status === 'FAIL';
                        const isPending = reading?.status === 'PENDING_REVIEW';
                        return (
                          <g
                            key={`${row.key}-${index}`}
                            onClick={() => {
                              if (!reading) return;
                              setActiveLocation(reading.groupId);
                              onEditMeasurement?.(reading.measurement);
                            }}
                            className={cn(reading && 'cursor-pointer')}
                          >
                            <circle
                              cx={crossSectionXs[index]}
                              cy={row.y}
                              r="5.5"
                              fill={isFail ? '#FBE8EA' : isPending ? '#FFF4D6' : '#FFFFFF'}
                              stroke={isFail ? '#C4001F' : isPending ? '#B38600' : '#4B5563'}
                              strokeWidth="1"
                            />
                            <text
                              x={crossSectionXs[index]}
                              y={row.y + 2.5}
                              fontFamily="monospace"
                              fontSize="5.8"
                              fontWeight="700"
                              textAnchor="middle"
                              fill={isFail ? '#C4001F' : isPending ? '#8A6500' : '#374151'}
                            >
                              {reading?.valueHv10 || '-'}
                            </text>
                          </g>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </svg>
              </div>
              <div className="mt-3 flex justify-between text-[11px] text-heritage-grey">
                <span>Configured threshold {overallLimit.text} - values above their zone limit fail.</span>
                <span>{zoneRows.length} zones x 5 indents</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead
              title="Active zone readings"
              subtitle={`${formatLabel(activeReadings[0]?.location || 'weld cap')} / ${HARDNESS_DETAIL.method}`}
              status={activeReadings.some((reading) => reading.status === 'FAIL') ? 'FAIL' : 'PASS'}
            />
            {activeReadings.map((reading) => {
              const zoneLimit = getLimitForZone(reading.location).value;
              const { min: scaleMin, max: scaleMax } = getHardnessScaleBounds(reading.valueHv10, zoneLimit);
              return (
              <TensileResultRow
                key={reading.id}
                label={getHardnessReadingLabel(reading)}
                formula={`project limit ${getLimitForZone(reading.location).text}`}
                value={reading.valueHv10}
                unit="HV10"
                min={scaleMin}
                max={scaleMax}
                hi={zoneLimit}
                status={reading.status}
              />
              );
            })}
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead
              title="Indent readings by zone"
              right={(
                <Button type="button" size="sm" onClick={() => onAddMeasurement?.('HARDNESS')} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Reading
                </Button>
              )}
            />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Reading</th>
                  <th className="px-4 py-3">Position</th>
                  <th className="px-4 py-3 text-right">Measured HV10</th>
                  <th className="px-4 py-3 text-right">Project limit</th>
                  <th className="px-4 py-3 text-right">Fallback limit</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {allReadings.map((reading) => (
                  <tr key={`${reading.groupId}-${reading.id}`} onClick={() => setActiveLocation(reading.groupId)} className={cn('cursor-pointer hover:bg-page-bg', (reading.groupId === activeLocation || reading.location === activeLocation) && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3">{formatLabel(reading.location)}</td>
                    <td className="px-4 py-3 font-mono font-semibold">{getHardnessReadingLabel(reading)}</td>
                    <td className="px-4 py-3">{reading.position}</td>
                    <td className="px-4 py-3 text-right font-mono">{reading.valueHv10}</td>
                    <td className="px-4 py-3 text-right font-mono">&lt;= {reading.projectLimit}</td>
                    <td className="px-4 py-3 text-right font-mono">&lt;= {reading.fallbackLimit}</td>
                    <td className="px-4 py-3"><StatusBadge status={reading.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <ItemActionButtons measurement={reading.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Limit decision" subtitle="Which limit applies depends on the sour-service flag in the project" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Card className="border-[#E2E5EA]">
              <CardContent className="p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">PSL2 standard</div>
                <div className="mt-2 font-heading text-[20px] font-semibold text-blueprint-blue">&lt;= {fallbackLimit} <span className="text-[11px] font-normal text-heritage-grey">HV10</span></div>
                <div className="mt-3 border-t border-[#EEF0F3] pt-3 text-[11px] text-heritage-grey">Measured max <span className="font-mono text-text-grey">{maxReading} HV10</span></div>
              </CardContent>
            </Card>
            <Card className="border-[#E7D28A] bg-[rgba(230,161,0,0.10)]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Sour service proposed</div>
                  <span className="rounded bg-white px-2 py-0.5 text-[10px] font-semibold text-blueprint-blue">active</span>
                </div>
                <div className="mt-2 font-heading text-[20px] font-semibold text-blueprint-blue">{overallLimit.text}</div>
                <div className="mt-3 border-t border-[#E7D28A] pt-3 text-[11px] text-heritage-grey">Measured max <span className="font-mono text-text-grey">{maxReading} HV10</span></div>
              </CardContent>
            </Card>
            <Card className="border-[#E2E5EA]">
              <CardContent className="p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">PSL1 non-sour</div>
                <div className="mt-2 font-heading text-[20px] font-semibold text-blueprint-blue">no limit</div>
                <div className="mt-3 border-t border-[#EEF0F3] pt-3 text-[11px] text-heritage-grey">Standard max <span className="font-mono text-text-grey">{maxReading} HV10</span></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', HARDNESS_DETAIL.reportId, true],
          ['Performed at', formatDisplayDate(HARDNESS_DETAIL.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', HARDNESS_DETAIL.lab],
          ['Technician', HARDNESS_DETAIL.technician],
          ['Specification', HARDNESS_DETAIL.spec],
          ['Source file', HARDNESS_DETAIL.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', frequency || 'per_heat', true], ['Lot', 'LOT-238'], ['Status', <StatusBadge key="status" status={overallStatus} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};

const hardnessLocationStatus = (location: typeof HARDNESS_DETAIL.locations[number]) => {
  if (location.readings.some((reading) => reading.status === 'FAIL')) return 'FAIL';
  if (location.readings.some((reading) => reading.status === 'PENDING_REVIEW')) return 'PENDING_REVIEW';
  return 'PASS';
};
